import { Controller } from "@hotwired/stimulus"
import { copyTextToClipboard } from "../lib/clipboard"

// Masks a secret value (e.g. a webhook auth_token), revealing it on demand and
// copying it to the clipboard. The plaintext lives only in the controller's
// `token` value — never in an <input value> attribute — so it is not selectable
// or visible until the agent explicitly reveals it.
export default class extends Controller {
  static targets = ["display", "icon", "toggle"]
  static values = {
    token: String,
    mask: { type: String, default: "••••••••••••••••" }
  }

  connect() {
    this.revealed = false
    this.render()
  }

  toggle(event) {
    event.preventDefault()
    this.revealed = !this.revealed
    this.render()
  }

  async copy(event) {
    event.preventDefault()
    // Capture the trigger synchronously — event.currentTarget is null after the await.
    const button = event.currentTarget
    // lib/clipboard covers the non-secure-context / permission-denied fallback
    // and resolves true/false without rejecting.
    const copied = await copyTextToClipboard(this.tokenValue)
    // Only confirm if the copy actually succeeded — a green check on a failed
    // copy is false confidence that the token reached the clipboard.
    if (copied) this.flashCopied(button)
  }

  render() {
    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = this.revealed ? this.tokenValue : this.maskValue
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("ph-eye", !this.revealed)
      this.iconTarget.classList.toggle("ph-eye-slash", this.revealed)
    }
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-pressed", this.revealed ? "true" : "false")
    }
  }

  flashCopied(button) {
    const icon = button.querySelector("i")
    if (!icon) return

    // Preserve the TRUE original (only on the first swap) and cancel any
    // in-flight restore, so a rapid second click can't leave the icon stuck on
    // ph-check (the second click would otherwise capture ph-check as "original").
    if (this.flashTimer) {
      clearTimeout(this.flashTimer)
    } else {
      this.flashOriginal = icon.className
    }
    icon.className = "ph ph-check"
    this.flashTimer = setTimeout(() => {
      icon.className = this.flashOriginal
      this.flashTimer = null
    }, 1200)
  }
}
